import type { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { CreateDisc } from "@babylonjs/core/Meshes/Builders/discBuilder";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import type { ZoneState } from "@arena/shared";

/** The banner's 64 × 128 sprite, in metres: as tall as a fighter drawn. */
const BANNER_WIDTH = 1.15;
const BANNER_HEIGHT = 2.3;

/**
 * Giovanna's planted banner (D47): the PixelLab banner upright where she
 * planted it, turned to the camera, and a golden ring on the floor as wide
 * as the area it shields. It fades in and out with the zone.
 */
export class BannerView {
  private readonly mesh: Mesh;
  private readonly material: StandardMaterial;
  private readonly texture: Texture;
  private readonly ring: Mesh;
  private readonly ringMaterial: StandardMaterial;
  private age = 0;

  constructor(scene: Scene) {
    this.mesh = CreatePlane("banner", { width: BANNER_WIDTH, height: BANNER_HEIGHT }, scene);
    this.mesh.billboardMode = Mesh.BILLBOARDMODE_Y;
    this.mesh.isPickable = false;
    this.texture = new Texture("/proto2d/giovanna/banner.png", scene, true, true, Texture.NEAREST_SAMPLINGMODE);
    this.texture.hasAlpha = true;
    this.material = new StandardMaterial("banner", scene);
    this.material.diffuseTexture = this.texture;
    this.material.useAlphaFromDiffuseTexture = true;
    this.material.emissiveColor = new Color3(0.75, 0.72, 0.68);
    this.material.specularColor = Color3.Black();
    this.material.backFaceCulling = false;
    this.mesh.material = this.material;
    this.ring = CreateDisc("banner-area", { radius: 1, tessellation: 48 }, scene);
    this.ring.rotation.x = Math.PI / 2;
    this.ring.isPickable = false;
    this.ringMaterial = new StandardMaterial("banner-area", scene);
    this.ringMaterial.disableLighting = true;
    this.ringMaterial.emissiveColor = new Color3(1, 0.82, 0.35);
    this.ringMaterial.backFaceCulling = false;
    this.ring.material = this.ringMaterial;
  }

  update(zone: ZoneState, dt: number): void {
    this.age += dt;
    // The pole's foot on the floor; the sprite's foot is its bottom edge.
    this.mesh.position.set(zone.x, zone.y + BANNER_HEIGHT / 2, zone.z);
    this.ring.position.set(zone.x, zone.y + 0.05, zone.z);
    this.ring.scaling.set(zone.radius, zone.radius, 1);
    const shown = Math.min(1, this.age / 0.2, zone.ticks / 12);
    this.material.alpha = shown;
    this.ringMaterial.alpha = shown * (0.12 + 0.04 * Math.sin(this.age * 3));
  }

  dispose(): void {
    this.mesh.dispose();
    this.material.dispose();
    this.texture.dispose();
    this.ring.dispose();
    this.ringMaterial.dispose();
  }
}
